import React, { useState } from 'react';
import { Plus, Trash2, GripVertical, Check, X, Sparkles, Crown, Zap, Star, TrendingUp, DollarSign, Calendar, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { FormField, TextInput, TextArea, Toggle, SelectInput } from './FormComponents';

export const PricingSection = ({ formData, onInputChange, templateType }) => {
  const [expandedPlan, setExpandedPlan] = useState(null);

  const getPricingValue = (field) => {
    return formData.pricingSettings?.[field] ?? '';
  };

  const setPricingValue = (field, value) => {
    onInputChange(`pricingSettings.${field}`, value);
  };

  const billingCycles = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
    { value: 'lifetime', label: 'Lifetime' }
  ];

  const planIcons = [
    { value: 'sparkles', label: 'Sparkles' },
    { value: 'crown', label: 'Crown' },
    { value: 'zap', label: 'Zap' },
    { value: 'star', label: 'Star' },
    { value: 'trending', label: 'Trending' }
  ];

  const addPlan = () => {
    const plans = getPricingValue('plans') || [];
    const newPlan = {
      id: `plan_${Date.now()}`,
      name: `Plan ${plans.length + 1}`,
      description: '',
      price: 0,
      billingCycle: 'monthly',
      icon: 'sparkles',
      featured: false,
      popular: false,
      features: [],
      limits: {
        maxUsers: null,
        maxStorage: null,
        maxProjects: null
      },
      customFields: {},
      active: true
    };
    setPricingValue('plans', [...plans, newPlan]);
    setExpandedPlan(newPlan.id);
  };

  const updatePlan = (planId, field, value) => {
    const plans = getPricingValue('plans') || [];
    const updatedPlans = plans.map(plan => 
      plan.id === planId ? { ...plan, [field]: value } : plan
    );
    setPricingValue('plans', updatedPlans);
  };

  const deletePlan = (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    const plans = getPricingValue('plans') || [];
    setPricingValue('plans', plans.filter(p => p.id !== planId));
    if (expandedPlan === planId) {
      setExpandedPlan(null);
    }
  };

  const addFeature = (planId) => {
    const plans = getPricingValue('plans') || [];
    const updatedPlans = plans.map(plan => {
      if (plan.id === planId) {
        return {
          ...plan,
          features: [...(plan.features || []), { id: `feat_${Date.now()}`, text: '', included: true }]
        };
      }
      return plan;
    });
    setPricingValue('plans', updatedPlans);
  };

  const updateFeature = (planId, featureId, field, value) => {
    const plans = getPricingValue('plans') || [];
    const updatedPlans = plans.map(plan => {
      if (plan.id === planId) {
        return {
          ...plan,
          features: (plan.features || []).map(feat => 
            feat.id === featureId ? { ...feat, [field]: value } : feat
          )
        };
      }
      return plan;
    });
    setPricingValue('plans', updatedPlans);
  };

  const deleteFeature = (planId, featureId) => {
    const plans = getPricingValue('plans') || [];
    const updatedPlans = plans.map(plan => {
      if (plan.id === planId) {
        return {
          ...plan,
          features: (plan.features || []).filter(f => f.id !== featureId)
        };
      }
      return plan;
    });
    setPricingValue('plans', updatedPlans);
  };

  const getIconComponent = (iconValue) => {
    const iconMap = {
      sparkles: Sparkles,
      crown: Crown,
      zap: Zap,
      star: Star,
      trending: TrendingUp
    };
    return iconMap[iconValue] || Sparkles;
  };

  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', ZAR: 'R'
  };

  const plans = getPricingValue('plans') || [];
  const currency = getPricingValue('currency') || 'USD';
  const currencySymbol = currencySymbols[currency] || '$';

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Subscription Pricing</h3>
        <p className="text-gray-600">
          Configure subscription plans and pricing tiers
        </p>
      </div>

      {/* Enable Pricing */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-5 w-5 text-blue-600" />
              <h4 className="text-lg font-semibold text-gray-900">Subscription Pricing</h4>
            </div>
            <p className="text-sm text-gray-600">
              Enable subscription-based pricing for recurring revenue
            </p>
          </div>
          <Toggle
            checked={getPricingValue('enabled')}
            onChange={(value) => setPricingValue('enabled', value)}
          />
        </div>
      </div>

      {getPricingValue('enabled') && (
        <>
          {/* Global Settings */}
          <div className="space-y-6">
            <h4 className="text-lg font-semibold text-gray-900">Global Settings</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField label="Currency">
                <SelectInput
                  value={currency}
                  onChange={(value) => setPricingValue('currency', value)}
                  options={[
                    { value: 'USD', label: 'US Dollar ($)' },
                    { value: 'EUR', label: 'Euro (€)' },
                    { value: 'GBP', label: 'British Pound (£)' },
                    { value: 'CAD', label: 'Canadian Dollar (C$)' },
                    { value: 'AUD', label: 'Australian Dollar (A$)' },
                    { value: 'ZAR', label: 'South African Rand (R)' }
                  ]}
                />
              </FormField>

              <FormField label="Grace Period (Days)">
                <TextInput
                  type="number"
                  value={getPricingValue('gracePeriodDays')}
                  onChange={(value) => setPricingValue('gracePeriodDays', parseInt(value) || 0)}
                  placeholder="3"
                  min="0"
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Toggle
                checked={getPricingValue('allowPlanSwitching')}
                onChange={(value) => setPricingValue('allowPlanSwitching', value)}
                label="Allow Plan Switching"
                description="Users can upgrade/downgrade anytime"
              />

              <Toggle
                checked={getPricingValue('prorateUpgrades')}
                onChange={(value) => setPricingValue('prorateUpgrades', value)}
                label="Prorate Upgrades"
                description="Credit unused time on upgrades"
              />

              <Toggle
                checked={getPricingValue('cancelAnytime')}
                onChange={(value) => setPricingValue('cancelAnytime', value)}
                label="Cancel Anytime"
                description="Allow subscription cancellation"
              />

              <Toggle
                checked={getPricingValue('showComparison')}
                onChange={(value) => setPricingValue('showComparison', value)}
                label="Show Comparison Table"
                description="Display feature comparison"
              />
            </div>
          </div>

          {/* Trial Settings */}
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-purple-600" />
              <h4 className="text-lg font-semibold text-gray-900">Free Trial</h4>
            </div>

            <Toggle
              checked={getPricingValue('trialEnabled')}
              onChange={(value) => setPricingValue('trialEnabled', value)}
              label="Enable Free Trial"
              description="Offer a trial period before charging"
            />

            {getPricingValue('trialEnabled') && (
              <div className="bg-purple-50 p-4 rounded-lg">
                <FormField label="Trial Duration (Days)">
                  <TextInput
                    type="number"
                    value={getPricingValue('trialDays')}
                    onChange={(value) => setPricingValue('trialDays', parseInt(value) || 0)}
                    placeholder="14"
                    min="1"
                  />
                </FormField>
              </div>
            )}
          </div>

          {/* Pricing Plans */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-semibold text-gray-900">Pricing Plans</h4>
              <button
                onClick={addPlan}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add Plan
              </button>
            </div>

            {plans.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">No pricing plans yet</p>
                <button
                  onClick={addPlan}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Create Your First Plan
                </button>
              </div>
            )}

            <div className="space-y-4">
              {plans.map((plan, planIndex) => {
                const IconComponent = getIconComponent(plan.icon);
                const isExpanded = expandedPlan === plan.id;

                return (
                  <div
                    key={plan.id}
                    className={`bg-white rounded-xl border-2 transition-all ${
                      plan.featured 
                        ? 'border-blue-500 shadow-lg' 
                        : plan.popular
                        ? 'border-purple-500 shadow-md'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {/* Plan Header - Always Visible */}
                    <div className="p-6 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          <div className={`p-3 rounded-lg ${
                            plan.featured ? 'bg-blue-100' : plan.popular ? 'bg-purple-100' : 'bg-gray-100'
                          }`}>
                            <IconComponent className={`h-6 w-6 ${
                              plan.featured ? 'text-blue-600' : plan.popular ? 'text-purple-600' : 'text-gray-600'
                            }`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <h5 className="text-lg font-semibold text-gray-900">{plan.name}</h5>
                              {plan.popular && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                                  Most Popular
                                </span>
                              )}
                              {plan.featured && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                                  Featured
                                </span>
                              )}
                            </div>
                            <div className="flex items-baseline gap-2 mt-1">
                              <span className="text-2xl font-bold text-gray-900">
                                {currencySymbol}{plan.price}
                              </span>
                              <span className="text-gray-500">
                                /{plan.billingCycle}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setExpandedPlan(isExpanded ? null : plan.id)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title={isExpanded ? 'Collapse' : 'Expand to edit'}
                          >
                            {isExpanded ? (
                              <ChevronUp className="h-5 w-5 text-gray-600" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-600" />
                            )}
                          </button>
                          <button
                            onClick={() => deletePlan(plan.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete plan"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Edit Form */}
                    {isExpanded && (
                      <div className="p-6 space-y-6 bg-gray-50">
                        {/* Basic Info */}
                        <div className="space-y-4">
                          <h6 className="font-semibold text-gray-900">Plan Details</h6>
                          
                          <FormField label="Plan Name">
                            <TextInput
                              value={plan.name}
                              onChange={(value) => updatePlan(plan.id, 'name', value)}
                              placeholder="Pro Plan"
                            />
                          </FormField>

                          <FormField label="Description">
                            <TextArea
                              value={plan.description}
                              onChange={(value) => updatePlan(plan.id, 'description', value)}
                              placeholder="Perfect for growing teams"
                              rows={2}
                            />
                          </FormField>

                          <div className="grid grid-cols-2 gap-4">
                            <FormField label="Price">
                              <TextInput
                                type="number"
                                value={plan.price}
                                onChange={(value) => updatePlan(plan.id, 'price', parseFloat(value) || 0)}
                                placeholder="29.99"
                                step="0.01"
                                min="0"
                              />
                            </FormField>

                            <FormField label="Billing Cycle">
                              <SelectInput
                                value={plan.billingCycle}
                                onChange={(value) => updatePlan(plan.id, 'billingCycle', value)}
                                options={billingCycles}
                              />
                            </FormField>
                          </div>

                          <FormField label="Plan Icon">
                            <SelectInput
                              value={plan.icon}
                              onChange={(value) => updatePlan(plan.id, 'icon', value)}
                              options={planIcons}
                            />
                          </FormField>
                        </div>

                        {/* Badges */}
                        <div className="space-y-4">
                          <h6 className="font-semibold text-gray-900">Badges</h6>
                          <div className="grid grid-cols-2 gap-4">
                            <Toggle
                              checked={plan.featured}
                              onChange={(value) => updatePlan(plan.id, 'featured', value)}
                              label="Featured Plan"
                            />
                            <Toggle
                              checked={plan.popular}
                              onChange={(value) => updatePlan(plan.id, 'popular', value)}
                              label="Most Popular"
                            />
                          </div>
                        </div>

                        {/* Features */}
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h6 className="font-semibold text-gray-900">Features</h6>
                            <button
                              onClick={() => addFeature(plan.id)}
                              className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                            >
                              <Plus className="h-3 w-3" />
                              Add Feature
                            </button>
                          </div>

                          <div className="space-y-2">
                            {(plan.features || []).map((feature) => (
                              <div key={feature.id} className="flex items-center gap-2 bg-white p-2 rounded-lg">
                                <button
                                  onClick={() => updateFeature(plan.id, feature.id, 'included', !feature.included)}
                                  className={`p-1 rounded transition-colors ${
                                    feature.included ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                                  }`}
                                  title={feature.included ? 'Included' : 'Not included'}
                                >
                                  {feature.included ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                                </button>
                                <TextInput
                                  value={feature.text}
                                  onChange={(value) => updateFeature(plan.id, feature.id, 'text', value)}
                                  placeholder="Feature description"
                                  className="flex-1"
                                />
                                <button
                                  onClick={() => deleteFeature(plan.id, feature.id)}
                                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                            {(!plan.features || plan.features.length === 0) && (
                              <p className="text-sm text-gray-500 text-center py-4">
                                No features added yet
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Limits */}
                        <div className="space-y-4">
                          <h6 className="font-semibold text-gray-900">Usage Limits</h6>
                          <div className="grid grid-cols-3 gap-3">
                            <FormField label="Max Users">
                              <TextInput
                                type="number"
                                value={plan.limits?.maxUsers || ''}
                                onChange={(value) => updatePlan(plan.id, 'limits', { 
                                  ...plan.limits, 
                                  maxUsers: value ? parseInt(value) : null 
                                })}
                                placeholder="Unlimited"
                                min="0"
                              />
                            </FormField>
                            <FormField label="Storage (GB)">
                              <TextInput
                                type="number"
                                value={plan.limits?.maxStorage || ''}
                                onChange={(value) => updatePlan(plan.id, 'limits', { 
                                  ...plan.limits, 
                                  maxStorage: value ? parseInt(value) : null 
                                })}
                                placeholder="Unlimited"
                                min="0"
                              />
                            </FormField>
                            <FormField label="Projects">
                              <TextInput
                                type="number"
                                value={plan.limits?.maxProjects || ''}
                                onChange={(value) => updatePlan(plan.id, 'limits', { 
                                  ...plan.limits, 
                                  maxProjects: value ? parseInt(value) : null 
                                })}
                                placeholder="Unlimited"
                                min="0"
                              />
                            </FormField>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                          <Toggle
                            checked={plan.active}
                            onChange={(value) => updatePlan(plan.id, 'active', value)}
                            label="Active"
                            description="Plan is visible to users"
                          />
                          <button
                            onClick={() => setExpandedPlan(null)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Done Editing
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};